import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
} from '@mui/material';
import { Collection } from '@domain/product/models/Collection';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import CollectionDetails from './CollectionDetails';
import CollectionRow from './CollectionRow';

interface CollectionsTableProps {
  collections: Collection[];
  onSelectionChange?: (selectedCollections: Collection[]) => void;
}

const CollectionsTable: React.FC<CollectionsTableProps> = ({
  collections,
  onSelectionChange,
}) => {
  const { openDrawer, closeDrawer } = useGenericDrawer();
  const [selectedCollections, setSelectedCollections] = useState<Collection[]>(
    []
  );

  const handleCollectionSelect = useCallback(
    (collection: Collection, selected: boolean) => {
      setSelectedCollections((prev) => {
        if (selected) {
          return [...prev, collection];
        }
        return prev.filter((c) => c.id !== collection.id);
      });
    },
    []
  );

  const handleSelectAll = useCallback((selected: boolean) => {
    if (selected) {
      setSelectedCollections([...collections]);
    } else {
      setSelectedCollections([]);
    }
  }, [collections]);

  const isAllSelected = useMemo(
    () =>
      collections.length > 0 && selectedCollections.length === collections.length,
    [collections.length, selectedCollections.length]
  );

  const isIndeterminate = useMemo(
    () =>
      selectedCollections.length > 0 &&
      selectedCollections.length < collections.length,
    [selectedCollections.length, collections.length]
  );

  useEffect(() => {
    onSelectionChange?.(selectedCollections);
  }, [selectedCollections, onSelectionChange]);

  const handleCollectionClick = (collection: Collection) => {
    openDrawer(
      'Collection Details',
      <CollectionDetails collectionId={collection.id} />,
      closeDrawer
    );
  };

  return (
    <TableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ px: 3 }}>Name</TableCell>
            <TableCell sx={{ px: 3 }}>Products</TableCell>
          </TableRow>
        </TableHead>
        <TableBody

        >
          {collections && collections.length > 0 ? (
            collections.map((collection) => (
              <CollectionRow
                key={collection.id}
                collection={collection}
                isSelected={selectedCollections.some((c) => c.id === collection.id)}
                onSelect={handleCollectionSelect}
                onCollectionClick={handleCollectionClick}
              />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                No collections found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CollectionsTable;
